<template>
  <div :class="b('', { variant: internalVariant })">
    <div :class="b('variant-toggle-container')">
      <span :class="b('variant-toggle-label')">
        {{ $t('c-download-list.variant-toggle-label') }}
      </span>
      <button
        type="button"
        :class="b('variant-toggle', { active: internalVariant === designVariant.Tile })"
        :aria-label="$t('c-download-list.variant-toggle-tiles-aria')"
        @click="changeVariant(designVariant.Tile)"
      >
        <e-icon
          icon="i-squares--four"
          size="18"
          aria-hidden="true"
        />
        {{ $t('c-download-list.variant-toggle-tiles') }}
      </button>
      <button
        type="button"
        :class="b('variant-toggle', { active: internalVariant === designVariant.List })"
        :aria-label="$t('c-download-list.variant-toggle-list-aria')"
        @click="changeVariant(designVariant.List)"
      >
        <e-icon
          icon="i-list"
          size="18"
          aria-hidden="true"
        />
        {{ $t('c-download-list.variant-toggle-list') }}
      </button>
    </div>
    <ul
      :class="b('list')"
      role="list"
    >
      <li
        v-for="(item, index) in items"
        :key="`download-item--${index}`"
        :class="b('item')"
        role="listitem"
      >
        <div
          v-if="internalVariant === designVariant.List"
          :class="b('item-file-type')"
        >
          <e-icon
            :class="b('item-icon')"
            icon="i-file--blank"
            aria-hidden="true"
            size="30"
          />
          <div
            v-if="item.fileType"
            :class="b('item-file-type-label')"
          >
            {{ item.fileType }}
          </div>
        </div>
        <e-picture
          v-if="item.image && internalVariant === designVariant.Tile"
          :class="b('item-image')"
          :srcset="item.image.srcset"
          :alt="item.image?.alt || item.image?.title || getSubtitle(item)"
          :title="item.image.title"
          :fallback="item.image.fallback"
          :ratio="getRatio(item)"
          :sizes="pictureSizes"
          loading="lazy"
        />
        <div
          v-if="item.assetType"
          :class="b('item-title')"
        >
          {{ item.assetType }}
        </div>
        <div
          v-if="!!getSubtitle(item)"
          :class="b('item-subtitle')"
        >
          {{ getSubtitle(item) }}
        </div>
        <a
          :class="b('item-link')"
          :href="item.url"
          download
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ $t('c-download-list.button-download-text') }}
          <e-icon
            icon="i-download"
            size="16"
            aria-hidden="true"
          />
        </a>
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
  import { PropType, defineComponent } from 'vue';
  import eIcon from '@/elements/e-icon.vue';
  import ePicture from '@/elements/e-picture.vue';
  import { DownloadFile } from '@/types/product';
  import { ImageSizes } from '@/types/sizes';

  export enum DesignVariant {
    Tile = 'tile',
    List = 'list',
  }

  type Setup = {
    designVariant: typeof DesignVariant;
  };

  type Data = {
    internalVariant: string;
    pictureSizes: ImageSizes;
  };

  /**
   * Renders a list of download links.
   */
  export default defineComponent({
    name: 'c-download-list',

    components: {
      eIcon,
      ePicture,
    },

    props: {
      /**
       * Items to be displayed.
       */
      items: {
        type: Array as PropType<DownloadFile[]>,
        default: () => [],
      },

      /**
       * Define how the list should be displayed.
       */
      variant: {
        type: String,
        default: DesignVariant.Tile,
        validator: (value: DesignVariant) => [DesignVariant.Tile, DesignVariant.List].includes(value),
      },
    },
    // emits: {},

    setup(): Setup {
      return {
        designVariant: DesignVariant,
      };
    },
    data(): Data {
      return {
        internalVariant: DesignVariant.Tile as string,
        pictureSizes: {
          xxs: 210,
          xs: 356,
          sm: 316,
          md: 280,
          lg: 376,
          fallback: 376,
        },
      };
    },

    // computed: {},
    watch: {
      variant(value: string) {
        this.internalVariant = value;
      },
    },

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      changeVariant(value: string) {
        this.internalVariant = value;
      },

      getSubtitle(item: DownloadFile): string {
        return item.name || item.fileName;
      },

      getRatio(item: DownloadFile): number {
        return item.fileType === 'pdf' ? 0 : 1.5;
      },
    },
    // render() {},
  });
</script>

<style lang="scss">
  @use 'sass:map';
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';

  .c-download-list {
    $this: &;

    &__variant-toggle-container {
      display: flex;
      align-items: center;
      place-content: flex-end;
      margin-bottom: 2rem;
      font-size: variables.$font-size--small;
    }

    &__variant-toggle-label {
      margin-right: 0.5rem;
    }

    &__variant-toggle {
      @include mixins.hover-effect--default;

      display: flex;
      align-items: center;
      cursor: pointer;
      gap: 10px;
      padding: 0.2rem 1rem;
      border: 1px solid variables.$va-color-grayscale--0;

      &--active {
        background-color: variables.$va-color-grayscale--0;
        color: variables.$va-color-grayscale--1000;
      }
    }

    &--variant-list {
      --c-download-list-item-background: var(--theme-color-grayscale--1000);

      #{$this}__list {
        display: flex;
        flex-direction: column;
        gap: 2rem;

        @include mixins.media(sm) {
          gap: 0;
        }
      }

      #{$this}__item {
        position: relative;
        height: 100%;
        display: grid;
        grid-template-areas:
          'icon title'
          'icon subtitle'
          'icon link';
        grid-template-columns: 40px 1fr;
        gap: 0.5rem 1rem;
        padding: 1rem;
        background-color: var(--c-download-list-item-background);

        @include mixins.media(sm) {
          grid-template-areas:
            'icon title link'
            'icon subtitle link';
          grid-template-columns: 40px 1fr 200px;
          align-items: flex-start;
          gap: 0 1rem;
          border-bottom: 1px dashed variables.$va-color-grayscale--800;
        }

        &:hover {
          --c-download-list-item-background: var(--theme-color-grayscale--950);
        }
      }

      #{$this}__item-file-type {
        grid-area: icon;
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        max-height: 30px;
      }

      #{$this}__item-file-type-label {
        background-color: var(--c-download-list-item-background);
        position: absolute;
        bottom: 0;
        width: 100%;
        text-align: center;
        font-size: variables.$font-size--smallest;
        line-height: 1;
        text-transform: uppercase;
        height: calc(#{variables.$font-size--smallest} + 4px);
        padding: 4px 0;
        overflow: hidden;
      }

      #{$this}__item-title {
        grid-area: title;
        font-weight: variables.$va-font-weight--bold;
        padding-bottom: 0.25rem;
      }

      #{$this}__item-subtitle {
        grid-area: subtitle;
      }

      #{$this}__item-title,
      #{$this}__item-subtitle {
        @include mixins.media(sm) {
          padding-right: 1rem;
          background-color: var(--c-download-list-item-background);
        }
      }

      #{$this}__item-link {
        @include mixins.hover-effect--default;

        grid-area: link;
        justify-self: flex-start;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        background-color: var(--c-download-list-item-background);
        padding: 0.3rem 0;

        @include mixins.media(sm) {
          place-self: center flex-end;
          padding: 0.3rem 0.6rem;
        }

        &::before {
          content: '';
          width: 100%;
          height: 100%;
          position: absolute;
          top: 0;
          left: 0;
        }
      }
    }

    &--variant-tile {
      $content-padding: 1rem;
      $content-line-height: 1.15;

      #{$this}__list {
        --max-items: 2;

        display: grid;
        gap: 1.5rem;
        grid-template-columns: repeat(
          auto-fit,
          calc(100% / var(--max-items) - (1.5rem * (var(--max-items) - 1) / var(--max-items)))
        );
        padding: 0.5rem;

        @include mixins.media(sm) {
          --max-items: 4;
        }

        @include mixins.media(md) {
          --max-items: 5;
        }

        @include mixins.media(lg) {
          --max-items: 6;
        }
      }

      #{$this}__item {
        @include mixins.hover-effect--default;

        position: relative;
        height: 100%;
        display: flex;
        flex-direction: column;
        background-color: variables.$va-color-grayscale--900;
        overflow-wrap: break-word;
        box-shadow: 0 -8px 50px -10px rgba(229, 229, 229, 80%);
      }

      #{$this}__item-image {
        max-height: 200px;
        min-height: 200px;
        display: flex;
        margin-bottom: $content-padding;
        background-color: var(--theme-color-grayscale--1000);
      }

      #{$this}__item-image img {
        object-fit: contain;
      }

      #{$this}__item-title {
        @include mixins.line-clamp(4, $content-line-height);

        font-weight: variables.$va-font-weight--bold;
        line-height: $content-line-height;
        margin-bottom: 0.5rem;
        padding: 0 $content-padding;
      }

      #{$this}__item-subtitle {
        @include mixins.line-clamp(4, $content-line-height);

        font-size: variables.$font-size--small;
        line-height: $content-line-height;
        margin-bottom: 2rem;
        padding: 0 $content-padding;
      }

      #{$this}__item-link {
        display: flex;
        align-items: center;
        place-self: flex-end;
        margin: auto $content-padding $content-padding;
        gap: 0.5rem;
        padding: 3px;
        font-size: variables.$font-size--small;

        @include mixins.media(sm) {
          font-size: variables.$font-size--regular;
        }

        &::before {
          content: '';
          width: 100%;
          height: 100%;
          position: absolute;
          top: 0;
          left: 0;
        }
      }
    }
  }
</style>
